/**
 * MagneticButton (по мотивам React Bits): обёртка слегка тянется за
 * курсором и пружинит обратно. Оборачивает любую кнопку.
 */

import { motion, useReducedMotion, useSpring } from 'framer-motion';
import { useRef, type MouseEvent, type ReactNode } from 'react';

interface MagneticButtonProps {
  readonly children: ReactNode;
  /** Доля смещения курсора от центра, которую повторяет элемент. */
  readonly strength?: number;
  readonly className?: string;
}

const SPRING = { stiffness: 220, damping: 18, mass: 0.4 } as const;

export function MagneticButton({ children, strength = 0.25, className }: MagneticButtonProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const x = useSpring(0, SPRING);
  const y = useSpring(0, SPRING);

  const onMove = (event: MouseEvent<HTMLDivElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (reduceMotion || rect === undefined) return;
    x.set((event.clientX - rect.left - rect.width / 2) * strength);
    y.set((event.clientY - rect.top - rect.height / 2) * strength);
  };

  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      className={['inline-block', className ?? ''].join(' ')}
      style={{ x, y }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </motion.div>
  );
}
